import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { SvgIcon, Tooltip, Typography } from '@mui/material';
import type { GetPurchaseOrders, PaymentSchedule, PaymentSchedulesItems } from 'api/types';
import EditIcon from 'assets/images/edit-icon.svg';
import Badge from 'components/NewLayout/Badge';
import DataTable from 'components/NewLayout/Table';
import React, { useEffect, useState } from 'react';
import { currencyFormat, dateFormatter, getAssociatedJobs, getPrice } from 'utils/index';
import type { DataTableObjectType } from 'utils/types';
import BillUpdateModal from './BillUpdateModal';

type PaginationData = {
  pageNumber: string;
  pageSize: string;
  searchText: string;
};
interface Bill {
  bill_id: number;
  purchase_order_id: string;
  status: string;
}
const PaymentScheduleTable = React.memo(function PaymentScheduleTable({
  loading,
  paymentSchedule,
  fetchAllPayments,
  totalCount,
  setPaginationData,
  onClickPurchaseOrderNumber,
  onClickScheduleEdit,
  parentComponent,
  handleSort
}: {
  loading: boolean;
  fetchAllPayments: () => void;
  paymentSchedule: PaymentSchedule[];
  totalCount: number;
  setPaginationData: (pagination: PaginationData) => void;
  onClickPurchaseOrderNumber: (row: GetPurchaseOrders) => void;
  onClickScheduleEdit: (row: GetPurchaseOrders) => void;
  parentComponent: 'OutstandingBills' | 'PaymentHistory';
  handleSort?: (order: 'asc' | 'desc', orderBy: string) => void;
}) {
  const [rowVisible, setRowVisible] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<GetPurchaseOrders | null>();
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [, setIsProjectManger] = useState<boolean>(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [, setIsBasicUser] = useState<boolean>(false);

  const toggleTr = (row: GetPurchaseOrders, index: number) => {
    const RV = index === rowVisible ? -1 : index;
    setRowVisible(RV);
    setSelectedPurchaseOrder(row);
  };

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  useEffect(() => {
    const userRoles = localStorage.getItem('userRoles');
    const userRolesArr: string[] = userRoles?.split(',') || [];
    setRoles(userRolesArr);
    if (userRolesArr.length === 1 && userRolesArr[0] === 'project_manager') {
      setIsProjectManger(true);
    }
    if (userRolesArr.length === 1 && userRolesArr[0] === 'basic_user') {
      setIsBasicUser(true);
    }
  }, []);

  const onClickUpdatePaymentItem = (item) => {
    setSelectedBill(item);
  };

  function getClosestScheduled(param) {
    if (!param || param.length === 0) return '';

    const currentDate = new Date();

    const closest = param.reduce((closestItem, currentItem) => {
      const currentDueDate = new Date(currentItem.due_date);
      const closestDueDate = new Date(closestItem.due_date);

      return Math.abs(currentDueDate.getTime() - currentDate.getTime()) <
        Math.abs(closestDueDate.getTime() - currentDate.getTime())
        ? currentItem
        : closestItem;
    });

    return dateFormatter(closest.due_date, 'date');
  }

  const ApprovedBillsBadge = ({ count }: { count: number }) => (
    <span
      style={{
        display: 'inline-block',
        background: count > 0 ? '#FFF3E0' : '#F5F5F5',
        color: count > 0 ? '#FF9800' : '#757575',
        borderRadius: 12,
        padding: '2px 12px',
        fontWeight: 600,
        fontSize: 14,
        minWidth: 42,
        textAlign: 'center',
        lineHeight: '20px',
        marginLeft: 2
      }}>
      {count}
    </span>
  );

  const columns: DataTableObjectType<GetPurchaseOrders>[] = [
    {
      prop: 's3_uri',
      name: '',
      data: (row, index) => {
        const i: number = parseInt(index?.toString()?.split('-')[0] || '-1');
        return (
          <div className="icon-div">
            <Tooltip title="Update Payment Schedule">
              <img
                src={EditIcon}
                onClick={() => onClickScheduleEdit(row)}
                className="link-icon"
                alt="Edit Icon"
              />
            </Tooltip>
            <Tooltip title={rowVisible === i ? 'Collapse' : 'Expand'}>
              <SvgIcon
                sx={{ cursor: 'pointer', marginTop: '8px' }}
                component={rowVisible == i ? KeyboardArrowDown : KeyboardArrowRight}
                onClick={() => toggleTr(row, parseInt(i.toString()))}
              />
            </Tooltip>
          </div>
        );
      }
    },
    {
      prop: 'purchase_order_number',
      data: (row) => (
        <Tooltip title="Update Purchase Order">
          <div className="icon-div">
            <Typography
              sx={{
                textDecoration: 'underline',
                textDecorationThickness: '1px',
                textUnderlineOffset: '3px',
                cursor: 'pointer'
              }}
              onClick={() => onClickPurchaseOrderNumber(row)}>
              {row.purchase_order_number}
            </Typography>
          </div>
        </Tooltip>
      ),
      name: 'Purchase Order Number',
      isSort: true
    },
    ...(parentComponent === 'OutstandingBills'
      ? [
          {
            prop: 'approved_bills_count',
            name: '# Approved Bills',
            isSort: true,
            data: (row) => {
              const count = Array.isArray(row?.paymentschedules)
                ? row.paymentschedules.filter(
                    (item: PaymentSchedulesItems) =>
                      item.status && item.status.toLowerCase() == 'approved for payment'
                  ).length
                : 0;
              return <ApprovedBillsBadge count={count} />;
            }
          }
        ]
      : []),
    {
      prop: 'total_price',
      data: (row) => currencyFormat(row.total_price || 0.0),
      name: 'Total PO Amount'
      // isSort: true
    },
    {
      prop: 'total_paid',
      data: (row) => currencyFormat(getPrice(row, 'total')),
      name: 'Total Paid'
      // isSort: true
    },
    {
      prop: 'total_remaining',
      data: (row) => currencyFormat(getPrice(row, 'remaining')),
      name: 'Total Remaining'
      // isSort: true
    },
    {
      prop: 'total_scheduled',
      data: (row) => currencyFormat(getPrice(row)),
      name: 'Total Scheduled'
      // isSort: true
    },
    {
      prop: 'associatedjob',
      data: (row) => getAssociatedJobs(row?.associatedjob || []),
      name: 'Associated Job'
    },
    {
      prop: 'closest_scheduled',
      data: (row) => getClosestScheduled(row.subRows),
      name: 'Closest Scheduled'
      // isSort: true
    },
    {
      prop: 'created_by',
      data: 'created_by',
      name: 'Created By'
      // isSort: true
    },
    {
      prop: 'vendor_name',
      data: (row) => <Typography>{row?.vendor?.vendor_name || ''}</Typography>,
      name: 'Vendor'
      // isSort: true
    },
    {
      prop: 'created_at',
      data: 'created_at',
      name: 'Created At',
      type: 'date',
      dateFormat: 'date'
      // isSort: true
    }
  ];

  const subColumns: DataTableObjectType<PaymentSchedulesItems>[] = [
    {
      prop: 'amount',
      name: 'Amount',
      data: (row) => currencyFormat(row.amount || 0)
    },
    {
      prop: 'due_date',
      data: (row) => (row.due_date ? dateFormatter(row.due_date, 'date') : ''),
      name: 'Purchase Date'
    },
    {
      prop: 'payment_method',
      data: 'payment_method',
      name: 'Payment Method'
    },
    {
      name: 'Status',
      prop: 'status',
      data: (row) => {
        const canEdit = ['admin', 'logistics', 'project_manager'].some((role) =>
          roles.includes(role)
        );

        const isEditable = row.status !== 'Paid';

        // Check for Pending status and updated_at time
        const isPending = row.status?.toLowerCase() === 'pending';
        let showEditIcon = canEdit && isEditable;
        let showFailedLabel = false;
        let showProcessingLabel = false; // Add this flag

        if (isPending && row.updated_at) {
          const updatedAt = new Date(row.updated_at);
          const now = new Date();
          const diffMs = now.getTime() - updatedAt.getTime();
          const diffMin = diffMs / (1000 * 60);

          if (diffMin < 10) {
            // Less than 10 min, show "Processing" and hide edit icon
            showEditIcon = false;
            showProcessingLabel = true;
          } else {
            // More than 10 min, show "Failed" and show edit icon
            showEditIcon = true;
            showFailedLabel = true;
          }
        }

        const editIcon = showEditIcon && (
          <div
            onClick={() => isEditable && onClickUpdatePaymentItem(row)}
            style={{
              cursor: isEditable ? 'pointer' : 'not-allowed',
              display: 'inline-block',
              margin: '0 0 6px 12px'
            }}
            className="icon-div"
            aria-disabled={!isEditable}
            title={!isEditable ? 'Editing is not allowed for paid items' : ''}>
            <Tooltip title="Update Status">
              <img
                style={{
                  pointerEvents: isEditable ? 'auto' : 'none'
                }}
                src={EditIcon}
                className="img-fluid link-icon"
                alt="Edit icon"
              />
            </Tooltip>
          </div>
        );

        const s = row.status?.toLowerCase();
        let statusType: 'success' | 'failed' | 'processing' = 'processing';
        if (s === 'paid') statusType = 'success';
        else if (s === 'not approved for payment' || s === 'not approved' || s === 'failed')
          statusType = 'failed';
        else if (showFailedLabel) statusType = 'failed';

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {showProcessingLabel ? (
              <Badge label="Processing" type="processing" />
            ) : showFailedLabel ? (
              <Badge label="Failed" type="failed" />
            ) : (
              s && <Badge label={row.status} type={statusType} />
            )}
            {editIcon}
          </div>
        );
      }
    },
    ...(parentComponent === 'PaymentHistory'
      ? [
          {
            prop: 'paid_by',
            name: 'Paid By',
            data: (row) => {
              const paidBy = row?.paid_by
                ? `${row.paid_by.first_name} ${row.paid_by.last_name}`
                : '';
              return (
                <Tooltip title={paidBy}>
                  <Typography>{paidBy}</Typography>
                </Tooltip>
              );
            }
          }
        ]
      : []),
    {
      prop: 'quickbooks_id',
      name: 'QBD ID',
      data: 'quickbooks_id'
    },
    {
      prop: 'last_updated_in_qbd',
      name: 'Last Updated in QBD',
      data: 'last_updated_in_qbd'
    }
  ];

  return (
    <>
      <DataTable<PaymentSchedule, PaymentSchedulesItems>
        columns={columns}
        subColumns={subColumns}
        items={paymentSchedule}
        rowVisible={rowVisible}
        loading={isLoading}
        isSorting={true}
        serverSidePagination
        totalCount={totalCount}
        dataSource={(pageNumber, pageSize, searchText) =>
          setPaginationData({
            pageNumber,
            pageSize,
            searchText
          })
        }
        handleSort={handleSort}
      />

      {selectedBill && selectedPurchaseOrder && (
        <BillUpdateModal
          selectedBill={selectedBill}
          onClose={() => {
            setSelectedBill(null);
          }}
          onComplete={() => fetchAllPayments()}
          selectedPurchaseOrder={selectedPurchaseOrder}
          setSelectedPurchaseOrder={setSelectedPurchaseOrder}
        />
      )}
    </>
  );
});

export default PaymentScheduleTable;
